import type { EvidenceSource } from '@cadencia/contracts';
import { rankedUsableSources, usableSources } from './source.js';

/**
 * Citas: qué puede citar una decisión y cómo se comprueba (§14 del encargo).
 *
 * EL PROBLEMA QUE ESTO RESUELVE
 *
 * «Las citas deben utilizar referencias estructuradas y verificadas. El modelo
 * debe referirse a fuentes recuperadas y registradas, no inventar enlaces
 * dentro del texto libre.»
 *
 * Este proyecto ya tenía media solución sin saberlo: `validateAIProse` rechaza
 * cualquier URL en el texto del modelo, y lo rechaza ENTERO —no lo limpia—
 * desde el 01/09/2026. El razonamiento de entonces sigue valiendo: «una URL en
 * la explicación de por qué subes de 12 a 14 series no puede venir de los
 * datos: solo puede estar inventada».
 *
 * Lo que faltaba es la otra mitad: un canal por el que las referencias que SÍ
 * se recuperaron entren como datos. Eso es una cita: **un identificador de
 * ficha**, no un enlace. El texto del modelo sigue sin poder llevar URLs, y las
 * referencias viajan aparte, resueltas contra la base.
 *
 * ASÍ UNA CITA INVENTADA ES IMPOSIBLE, no improbable: si el identificador no
 * está en la base, no se resuelve y la cita se cae. El modelo no puede fabricar
 * una fuente porque no puede fabricar una fila que él no escribió.
 */

export interface CitationVerdict {
  /** Las fichas citadas que existen, están activas y respaldan la afirmación. */
  resolved: EvidenceSource[];
  /**
   * Identificadores citados que no se pudieron resolver, con el motivo.
   *
   * Se devuelven en vez de ignorarse: cuántas veces el modelo cita algo que no
   * existe dice algo sobre el prompt que no se puede saber de otra forma, y es
   * exactamente el tipo de fallo que desaparece si se limpia en silencio.
   */
  rejected: { id: string; reason: string }[];
}

/**
 * Resuelve las citas de una decisión contra la base.
 *
 * `claim` acota: no basta con que la ficha exista, tiene que respaldar LA
 * AFIRMACIÓN que se está citando. Sin eso, el modelo podría citar un
 * metaanálisis sobre descansos entre series para justificar un cambio de
 * frecuencia, y la cita resolvería: existe, está activa, es buena. Y no dice
 * nada de lo que se está afirmando.
 */
export function resolveCitations(
  citedIds: readonly string[],
  claim: string,
  sources: readonly EvidenceSource[],
): CitationVerdict {
  const disponibles = new Map(usableSources(sources).map((fila) => [fila.id, fila]));
  const todas = new Map(sources.map((fila) => [fila.id, fila]));

  const resolved: EvidenceSource[] = [];
  const rejected: { id: string; reason: string }[] = [];
  const vistos = new Set<string>();

  for (const id of citedIds) {
    if (vistos.has(id)) {
      rejected.push({ id, reason: 'citada dos veces' });
      continue;
    }
    vistos.add(id);

    const usable = disponibles.get(id);
    if (usable === undefined) {
      // Se distingue «no existe» de «existe pero no se puede usar». La primera
      // es una cita inventada; la segunda es una fuente retractada o superada,
      // y saber cuál de las dos ha pasado cambia lo que hay que hacer.
      const conocida = todas.get(id);
      rejected.push({
        id,
        reason:
          conocida === undefined
            ? 'no existe en la base de evidencia'
            : `no utilizable: ${conocida.status}`,
      });
      continue;
    }

    if (!usable.claims.includes(claim)) {
      rejected.push({ id, reason: `no respalda «${claim}»` });
      continue;
    }

    resolved.push(usable);
  }

  return { resolved, rejected };
}

/**
 * Cómo se nombra una ficha cuando se enseña junto a una decisión.
 *
 * Formato corto y verificable: autores, año y el identificador con el que
 * cualquiera puede ir al documento. **Sin enlace activo**, por lo mismo que
 * `validateAIProse` los rechaza: el texto que rodea a esto lo escribió un
 * modelo, y un ancla dentro de esa prosa no se distingue de una inventada. El
 * identificador se copia y se pega, que es feo y es honesto.
 */
export function citationLabel(source: EvidenceSource): string {
  const identificador =
    source.doi !== null
      ? `doi:${source.doi}`
      : source.pmid !== null
        ? `PMID ${source.pmid}`
        : (source.url ?? 'sin identificador');
  return `${source.authors} (${String(source.year)}). ${source.publication}. ${identificador}`;
}

/**
 * La advertencia que acompaña a una cita cuando su fuente no es lo que parece.
 *
 * `null` cuando no hay nada que matizar. Existe porque el §8 prohíbe presentar
 * como algo que no es: una prepublicación, una muestra cuyo estatus de
 * sustancias nadie verificó, o una extrapolación desde otra población. Las tres
 * son citables —no se ocultan— pero no calladas.
 */
export function citationCaveat(source: EvidenceSource): string | null {
  const avisos: string[] = [];

  if (!source.peerReviewed) avisos.push('sin revisión por pares');
  if (source.sourceType === 'preprint') avisos.push('prepublicación');
  if (source.substanceStatus === 'undocumented') {
    avisos.push('no consta si la muestra era libre de sustancias');
  }
  if (source.substanceStatus === 'documented_enhanced') {
    avisos.push('muestra con uso declarado de sustancias');
  }
  if (source.applicability === 'indirect') avisos.push('población no equivalente');
  if (!source.fullTextAccess) avisos.push('leído solo el resumen');

  return avisos.length === 0 ? null : avisos.join('; ');
}

// ---------------------------------------------------------------------------
// Referencias cortas: cómo viaja la evidencia a la IA y cómo vuelve
// ---------------------------------------------------------------------------

/**
 * Cuántas fichas viajan con una propuesta de bloque, y cuánto de sus hallazgos.
 * CONVENCIÓN DECLARADA, fijada por el tope de contexto de CA-7.6.
 *
 * Medido el 22/09/2026 con `estimateContextTokens`, en el peor caso que fija
 * `blockProposalService.test.ts` —catálogo entero, seis fichas con hallazgos y
 * afirmaciones al máximo, seis observaciones de memoria, seis músculos
 * priorizados—: con 240 caracteres, 3.966 tokens de 4.000, sin margen para
 * nada; con 160, 3.799. El hallazgo recortado es un recordatorio para el
 * modelo, no la ficha: la ficha entera la ve la persona en Evidencia.
 */
export const PLANNING_EVIDENCE = { maxSources: 6, findingChars: 160 } as const;

export interface PlanningEvidence {
  /** Lo que va en el contexto. Sin identificadores de fila. */
  context: {
    ref: string;
    claims: string[];
    certainty: string;
    applicability: string;
    finding: string;
  }[];
  /** «E1» → identificador de la ficha. Se queda en el dispositivo. */
  refs: ReadonlyMap<string, string>;
}

/**
 * Numera las fichas activas para mandarlas a la IA.
 *
 * El identificador de la fila no sale: `auditAIContext` rechaza `id` y todo lo
 * que termine en `Id`, y aunque no lo hiciera, un UUID no le dice nada al
 * modelo y cuesta tokens. La referencia corta es local a UNA petición: «E1» de
 * hoy no es «E1» de mañana, y por eso lo que se guarda al volver es el
 * identificador ya traducido, nunca la referencia.
 */
export function evidenceForPlanning(sources: readonly EvidenceSource[]): PlanningEvidence {
  const elegidas = rankedUsableSources(sources).slice(0, PLANNING_EVIDENCE.maxSources);
  const refs = new Map<string, string>();
  const context = elegidas.map((fila, indice) => {
    const ref = `E${String(indice + 1)}`;
    refs.set(ref, fila.id);
    const recortado =
      fila.findings.length > PLANNING_EVIDENCE.findingChars
        ? `${fila.findings.slice(0, PLANNING_EVIDENCE.findingChars - 1)}…`
        : fila.findings;
    return {
      ref,
      claims: [...fila.claims],
      certainty: fila.certainty,
      applicability: fila.applicability,
      finding: recortado,
    };
  });
  return { context, refs };
}

export interface ResolvedCitation {
  sourceId: string;
  claim: string;
  /** `citationLabel`, fijado al resolver: la ficha puede cambiar después. */
  label: string;
  caveat: string | null;
}

/**
 * Traduce las citas que devolvió la IA y las pasa por `resolveCitations`.
 *
 * Una referencia que no estaba en el contexto se rechaza como inventada, antes
 * de llegar a la base: no hay fila que buscar. Las demás se resuelven una a una
 * contra su afirmación, porque cada cita respalda UNA afirmación y una ficha
 * válida para otra no vale para esta.
 */
export function resolveCitationRefs(
  citations: readonly { ref: string; claim: string }[],
  refs: ReadonlyMap<string, string>,
  sources: readonly EvidenceSource[],
): { resolved: ResolvedCitation[]; rejected: { ref: string; reason: string }[] } {
  const resolved: ResolvedCitation[] = [];
  const rejected: { ref: string; reason: string }[] = [];
  const vistas = new Set<string>();

  for (const cita of citations) {
    const clave = `${cita.ref}|${cita.claim}`;
    if (vistas.has(clave)) {
      rejected.push({ ref: cita.ref, reason: 'citada dos veces' });
      continue;
    }
    vistas.add(clave);

    const id = refs.get(cita.ref);
    if (id === undefined) {
      rejected.push({ ref: cita.ref, reason: 'no estaba entre las fichas enviadas' });
      continue;
    }
    const veredicto = resolveCitations([id], cita.claim, sources);
    const fuente = veredicto.resolved[0];
    if (fuente === undefined) {
      rejected.push({ ref: cita.ref, reason: veredicto.rejected[0]?.reason ?? 'no resuelta' });
      continue;
    }
    resolved.push({
      sourceId: fuente.id,
      claim: cita.claim,
      label: citationLabel(fuente),
      caveat: citationCaveat(fuente),
    });
  }
  return { resolved, rejected };
}
